// Client-side photo handling: decode an image file, downscale it, and re-encode
// it as a JPEG data URL small enough to store inside a submission.
export const MAX_PHOTO_EDGE_PX = 1600;
export const JPEG_QUALITY = 0.85;
export const MAX_PHOTO_BYTES = 4 * 1024 * 1024;

const readImageBitmap = async (file) => {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall through to <img> fallback (Safari can throw on some inputs)
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const compressImage = async (file) => {
  const bitmap = await readImageBitmap(file);
  const width = bitmap.width || bitmap.naturalWidth;
  const height = bitmap.height || bitmap.naturalHeight;
  const longest = Math.max(width, height);
  const scale = longest > MAX_PHOTO_EDGE_PX ? MAX_PHOTO_EDGE_PX / longest : 1;
  const targetW = Math.round(width * scale);
  const targetH = Math.round(height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  if (typeof bitmap.close === 'function') bitmap.close();

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
      'image/jpeg',
      JPEG_QUALITY,
    );
  });

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
};
